import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useParser } from '../../state/parser-hook';
import { useSiteContext } from '../../state/SiteContext';
import { isClient, services } from '../../globals';
import { useLangFilter, useSystemPropertiesFilter } from '../../state/FilterContext';
import { useValidityFilter } from '../../state/FilterContext';
import { TextExtractor, findFirstComponentOfType } from '../internal/TextExtractor';

interface IncludeImageProps {
  alt: string,
  width: string,
  height: string,
  inline: boolean,
  children: React.ReactNode,
}

/***
 * A component that renders an image.
 * For most images this is not needed, but for SVG images it is needed to render them inline.
 * Otherwise just use an img tag directly and use the "DirectLinkValueModifier" in the src attribute
 * to get the correct URL.
 */
export function IncludeImage({ alt, width, height, inline, children }: IncludeImageProps): React.ReactElement {
  const parse = useParser();

  const [href, setHref] = useState<string>();
  const [svg, setSvg] = useState<React.ReactNode>();

  // Get the path from the path child
  const pathChild = useMemo(() => findFirstComponentOfType(children, "arg-path"), [children]);
  const [pathValue, setPathValue] = useState("");

  const isSvg = pathValue.endsWith(".svg");

  const { siteState } = useSiteContext();
  const validity = useValidityFilter();
  const system = useSystemPropertiesFilter();
  const lang = useLangFilter();

  useEffect(() => {
    if (siteState.site) {
      if (siteState.site.name && pathValue) {
        setHref(services.file.getDirectLink(siteState.site.name,
          pathValue, validity, system, lang));
      }
    }
  }, [pathValue, siteState.site, validity, system, lang])

  useEffect(() => {
    async function loadSvg(): Promise<void> {
      if (services && isClient && href) {
        const svgText = await services.file.getTextfile(href);
        const svg = parse(svgText);
        setSvg(svg);
      }
    }
    if (isSvg) {
      loadSvg();
    }
  }, [href, isSvg, parse])

  const image = useMemo(() => {
    if (isSvg && svg && inline) {
      return <>{svg}</>
    }
    else {
      return <img
        src={href}
        alt={alt}
        height={height || ''}
        width={width || ''}
        loading={"lazy"} />
    }
  }, [isSvg, svg, inline, href, alt, height, width]);

  return <>
    {pathChild && (
      <TextExtractor onExtractedText={setPathValue}>
        {pathChild?.props.children}
      </TextExtractor>
    )}
    {image}
  </>;
}